import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import api from '../utils/api';

const Icon = ({ name, size = 16, color = 'currentColor' }) => {
  const icons = {
    refresh: <><path d="M1 4s0-3 3-3c3 0 5 2 5 2"/><path d="M1 4v4h4"/><path d="M15 12s0 3-3 3c-3 0-5-2-5-2"/><path d="M15 12v-4h-4"/></>,
    file:    <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></>,
    clock:   <><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></>,
    eye:     <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>,
    flame:   <><path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/></>,
    globe:   <><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></>,
    users:   <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>,
    shield:  <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></>,
    chevron: <><polyline points="6 9 12 15 18 9"/></>,
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      style={{ flexShrink: 0 }}>
      {icons[name]}
    </svg>
  );
};

function getReadLevel(seconds, maxSeconds) {
  if (!seconds || seconds === 0) return { label: 'Non lu',  color: '#555',    bar: '#333333' };
  const ratio = seconds / maxSeconds;
  if (ratio < 0.15) return { label: 'Peu lu',  color: '#d4f535', bar: '#d4f535' };
  if (ratio < 0.4)  return { label: 'Lu',      color: '#ff9500', bar: '#ff9500' };
  if (ratio < 0.75) return { label: 'Bien lu', color: '#ff6b6b', bar: '#ff6b6b' };
  return               { label: 'Très lu', color: '#ff4444', bar: '#ff4444' };
}

function formatDuration(seconds) {
  if (!seconds) return '0s';
  if (seconds < 60) return seconds + 's';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m + 'min' + (s > 0 ? ' ' + s + 's' : '');
}

export default function HeatmapPage() {
  const [data, setData]         = useState({ docs: [], global: [], byMember: [] });
  const [loading, setLoading]   = useState(true);
  const [view, setView]         = useState('global');
  const [selectedDoc, setSelectedDoc] = useState('');
  const [docSearch, setDocSearch] = useState('');
  const [showDocList, setShowDocList] = useState(false);
  const [lastUpdate, setLastUpdate]   = useState(new Date());

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/track/heatmap-sessions');
      const d = res.data || { docs: [], global: [], byMember: [] };
      setData(d);
      if (d.docs.length > 0) setSelectedDoc(prev => prev && d.docs.includes(prev) ? prev : d.docs[0]);
      setLastUpdate(new Date());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Filtrer par document sélectionné
  const globalRows  = data.global.filter(r => r.document_name === selectedDoc);
  const memberRows  = data.byMember.filter(r => r.document_name === selectedDoc);

  // Stats globales pour ce document
  const totalPages  = new Set(globalRows.map(r => r.page_number)).size;
  const totalTime   = globalRows.reduce((a, r) => a + Number(r.total_time || 0), 0);
  const totalVisits = globalRows.reduce((a, r) => a + Number(r.total_visits || 0), 0);
  const maxTime     = Math.max(...globalRows.map(r => Number(r.total_time || 0)), 1);
  const topPage     = globalRows.reduce((best, r) => (!best || Number(r.total_time) > Number(best.total_time)) ? r : best, null);

  // Vue par membre
  const memberMap = {};
  memberRows.forEach(r => {
    const key = r.member_name || r.member_email || 'Inconnu';
    if (!memberMap[key]) memberMap[key] = { name: key, pages: [] };
    memberMap[key].pages.push(r);
  });
  const members = Object.values(memberMap);

  return (
    <div style={{ padding: 16, background: '#0a0a0a', minHeight: '100vh' }}>
      {/* Breadcrumb */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 10, color: '#555', fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 6 }}>
          Administration / Heatmap Lecture
        </div>
        <h1 style={{ fontSize: 28, fontWeight: 900, color: '#ffffff', letterSpacing: -1, marginBottom: 4 }}>
          Heatmap <span style={{ color: '#d4f535' }}>lecture</span>
        </h1>
        <p style={{ color: '#555', fontSize: 12 }}>Analyse des pages lues par document et par membre</p>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 14, paddingTop: 14, borderTop: '1px solid #1a1a1a' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#d4f535', boxShadow: '0 0 8px #d4f53599' }} />
            <span style={{ fontSize: 11, color: '#888', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>
              Mise à jour · {format(lastUpdate, 'dd MMM. yyyy').toUpperCase()}
            </span>
          </div>
          <button onClick={fetchData} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10, border: '1.5px solid #333', background: '#111', color: '#ffffff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
            <Icon name="refresh" size={14} color="#ffffff" /> Actualiser
          </button>
        </div>
      </div>

      {/* Sélecteur document */}
      {data.docs.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 11, color: '#555', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>
            <Icon name="file" size={12} color="#555" /> &nbsp;Document ({data.docs.length})
          </div>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder={selectedDoc || 'Rechercher un document…'}
              value={docSearch}
              onFocus={() => setShowDocList(true)}
              onBlur={() => setTimeout(() => setShowDocList(false), 150)}
              onChange={e => { setDocSearch(e.target.value); setShowDocList(true); }}
              style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1.5px solid ' + (showDocList ? '#d4f535' : '#333'), background: '#111', color: '#ffffff', fontSize: 12, outline: 'none', boxSizing: 'border-box', fontWeight: 600 }}
            />
            {showDocList && (
              <div style={{ position: 'absolute', top: '110%', left: 0, right: 0, background: '#111', border: '1.5px solid #d4f53555', borderRadius: 12, zIndex: 100, maxHeight: 220, overflowY: 'auto', boxShadow: '0 8px 32px #00000099' }}>
                {data.docs.filter(d => d.toLowerCase().includes(docSearch.toLowerCase())).length === 0 ? (
                  <div style={{ padding: '12px 14px', color: '#555', fontSize: 12 }}>Aucun résultat</div>
                ) : data.docs.filter(d => d.toLowerCase().includes(docSearch.toLowerCase())).map(doc => (
                  <div key={doc} onMouseDown={() => { setSelectedDoc(doc); setDocSearch(''); setShowDocList(false); }}
                    style={{ padding: '10px 14px', borderBottom: '1px solid #1a1a1a', color: selectedDoc === doc ? '#d4f535' : '#aaa', background: selectedDoc === doc ? '#1a1a00' : 'transparent', fontSize: 12, fontWeight: selectedDoc === doc ? 800 : 500, cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {doc}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Toggle vue */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 20 }}>
        {[
          { id: 'global', label: 'Vue globale', icon: 'globe' },
          { id: 'member', label: 'Par membre',  icon: 'users' },
        ].map(t => (
          <button key={t.id} onClick={() => setView(t.id)}
            style={{ padding: '12px', borderRadius: 12, border: view === t.id ? 'none' : '1.5px solid #222', background: view === t.id ? '#d4f535' : 'transparent', color: view === t.id ? '#0a0a0a' : '#555', fontSize: 13, fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <Icon name={t.icon} size={15} color={view === t.id ? '#0a0a0a' : '#555'} />
            {t.label}
          </button>
        ))}
      </div>

      {/* Stats */}
      <div style={{ borderTop: '1px solid #1a1a1a', borderBottom: '1px solid #1a1a1a', padding: '16px 0', marginBottom: 20 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          {[
            { icon: 'file',  label: 'Pages lues',         value: totalPages,                color: '#d4f535', big: true },
            { icon: 'clock', label: 'Temps cumulé',        value: formatDuration(totalTime), color: '#ffffff', big: false },
            { icon: 'eye',   label: 'Visites totales',     value: totalVisits,               color: '#ffffff', big: true },
            { icon: 'flame', label: 'Page la plus lue',    value: topPage ? String(topPage.page_number).padStart(2,'0') : '—', color: '#ff4444', big: true },
          ].map((s, i) => (
            <div key={i}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                <Icon name={s.icon} size={13} color="#555" />
                <span style={{ fontSize: 11, color: '#555' }}>{s.label}</span>
              </div>
              <div style={{ fontSize: s.big ? 36 : 26, fontWeight: 900, color: s.color, lineHeight: 1 }}>{s.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Titre section */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <h2 style={{ fontSize: 15, fontWeight: 800, color: '#ffffff' }}>
          {view === 'global' ? 'Temps total par page · tous membres' : 'Temps par page · par membre'}
        </h2>
        <span style={{ fontSize: 10, color: '#555', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>
          {view === 'global' ? 'CUMULÉ' : 'INDIVIDUEL'}
        </span>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#555', fontSize: 13 }}>Chargement…</div>
      ) : !selectedDoc || globalRows.length === 0 ? (
        <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 12, padding: 24, textAlign: 'center' }}>
          <Icon name="eye" size={32} color="#333" />
          <div style={{ color: '#555', marginTop: 10, fontSize: 13 }}>Aucune donnée pour ce document</div>
        </div>
      ) : view === 'global' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {globalRows.sort((a,b) => a.page_number - b.page_number).map((page, i) => {
            const lvl = getReadLevel(Number(page.total_time), maxTime);
            const barW = Math.round((Number(page.total_time) / maxTime) * 100);
            const num = String(page.page_number).padStart(2, '0');
            return (
              <div key={i} style={{ background: '#111', border: '1px solid #1e1e1e', borderLeft: `4px solid ${lvl.bar}`, borderRadius: 14, padding: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ fontSize: 11, color: '#555', fontWeight: 700, fontFamily: 'monospace' }}>{num}</span>
                  <span style={{ fontSize: 11, fontWeight: 800, color: lvl.color }}>{lvl.label}</span>
                </div>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 16, fontWeight: 800, color: '#ffffff', marginBottom: 6 }}>Page {page.page_number}</div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <span style={{ fontSize: 38, fontWeight: 900, color: lvl.bar, lineHeight: 1 }}>{formatDuration(Number(page.total_time))}</span>
                    <span style={{ fontSize: 13, color: '#555', marginLeft: 4 }}>{page.total_visits} visite{page.total_visits > 1 ? 's' : ''} · {page.member_count} membre{page.member_count > 1 ? 's' : ''}</span>
                  </div>
                </div>
                <div style={{ background: '#1e1e1e', borderRadius: 999, height: 6, overflow: 'hidden' }}>
                  <div style={{ height: '100%', borderRadius: 999, background: lvl.bar, width: barW + '%', boxShadow: `0 0 8px ${lvl.bar}88` }} />
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {members.length === 0 ? (
            <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 12, padding: 24, textAlign: 'center' }}>
              <div style={{ color: '#555', fontSize: 13 }}>Aucun membre pour ce document</div>
            </div>
          ) : members.map((m, mi) => {
            const mMax = Math.max(...m.pages.map(p => Number(p.total_time||0)), 1);
            const mTotal = m.pages.reduce((a, p) => a + Number(p.total_time||0), 0);
            return (
              <div key={mi}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: '#111', border: '1px solid #1e1e1e', borderRadius: 10, marginBottom: 10 }}>
                  <div style={{ width: 34, height: 34, borderRadius: 10, background: '#1a1a00', border: '1px solid #d4f53533', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 900, color: '#d4f535' }}>
                    {m.name.charAt(0).toUpperCase()}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: '#ffffff' }}>{m.name}</div>
                    <div style={{ fontSize: 11, color: '#555' }}>{m.pages.length} page{m.pages.length > 1 ? 's' : ''} · {formatDuration(mTotal)} total</div>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {m.pages.sort((a,b) => a.page_number - b.page_number).map((p, pi) => {
                    const lvl = getReadLevel(Number(p.total_time||0), mMax);
                    const barW = Math.round((Number(p.total_time||0) / mMax) * 100);
                    return (
                      <div key={pi} style={{ background: '#0d0d0d', border: '1px solid #1a1a1a', borderLeft: `3px solid ${lvl.bar}`, borderRadius: 10, padding: '12px 14px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                          <span style={{ fontSize: 13, fontWeight: 800, color: '#ffffff' }}>Page {p.page_number}</span>
                          <span style={{ fontSize: 11, fontWeight: 700, color: lvl.color }}>{lvl.label}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 8 }}>
                          <span style={{ fontSize: 26, fontWeight: 900, color: lvl.bar }}>{formatDuration(Number(p.total_time||0))}</span>
                          <span style={{ fontSize: 11, color: '#555', marginLeft: 4 }}>{p.total_visits} visite{p.total_visits > 1 ? 's' : ''}</span>
                        </div>
                        <div style={{ background: '#1e1e1e', borderRadius: 999, height: 5, overflow: 'hidden' }}>
                          <div style={{ height: '100%', borderRadius: 999, background: lvl.bar, width: barW + '%' }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Légende */}
      {globalRows.length > 0 && (
        <div style={{ marginTop: 20, marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center' }}>
            {[
              { label: 'Non lu',  color: '#555' },
              { label: 'Peu lu',  color: '#d4f535' },
              { label: 'Lu',      color: '#ff9500' },
              { label: 'Bien lu', color: '#ff6b6b' },
              { label: 'Très lu', color: '#ff4444' },
            ].map(l => (
              <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#888' }}>
                <div style={{ width: 10, height: 10, borderRadius: 2, background: l.color }} />
                {l.label}
              </div>
            ))}
          </div>
          <div style={{ fontSize: 10, color: '#444', marginTop: 8 }}>
            Niveaux relatifs au temps max · vue globale = cumul tous membres · vue membre = temps individuel
          </div>
        </div>
      )}

      {/* Insight top page */}
      {topPage && (
        <div style={{ borderTop: '1px solid #1a1a1a', paddingTop: 16, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: '#1a0500', border: '1px solid #ff444433', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Icon name="flame" size={16} color="#ff4444" />
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 800, color: '#ffffff', marginBottom: 4 }}>
                Page {topPage.page_number} · intensité la plus élevée
              </div>
              <div style={{ fontSize: 12, color: '#666', lineHeight: 1.6 }}>
                {formatDuration(Number(topPage.total_time))} sur {formatDuration(totalTime)} cumulées · {totalPages} page{totalPages > 1 ? 's' : ''} lues
              </div>
            </div>
          </div>
        </div>
      )}

      <div style={{ borderTop: '1px solid #1a1a1a', paddingTop: 16, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <Icon name="shield" size={15} color="#333" />
        <div style={{ fontSize: 11, color: '#444', lineHeight: 1.6 }}>
          Données issues des sessions de lecture · vue globale = cumul de tous les membres · vue membre = temps individuel par page
        </div>
      </div>
    </div>
  );
}
